import { useEffect } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Download, Shield, ArrowLeft } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { PRIVACY_LAST_UPDATE } from "@/config/legalMeta";
import PrivacyRegistry from "@/components/PrivacyRegistry";
import { printPolicyPage } from "@/lib/printPolicy";

const handleDownload = (title) => printPolicyPage({ title, contentElementId: "policy-content" });

export default function PrivacyPolicyPage() {
  const [params] = useSearchParams();
  useEffect(() => {
    if (params.get("download") === "1") {
      const timer = setTimeout(() => handleDownload("Privacy Policy - Colucci Assicuratori"), 600);
      return () => clearTimeout(timer);
    }
  }, [params]);
  return (
    <>
      <Header />
      <main className="min-h-screen bg-[#FCFCFA] pt-20" data-testid="privacy-policy-page">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 py-12">
          <div className="flex items-center justify-between mb-8">
            <Link to="/" className="text-sm text-[#4A5E51] hover:text-[#163321] flex items-center gap-1">
              <ArrowLeft className="w-4 h-4" /> Torna alla Home
            </Link>
            <Button onClick={() => handleDownload("Privacy Policy - Colucci Assicuratori")} variant="outline" className="rounded-xl border-[#E5E2DB] text-sm gap-2" data-testid="download-privacy-btn">
              <Download className="w-4 h-4" /> Scarica PDF
            </Button>
          </div>

          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-[#163321]/10 rounded-full flex items-center justify-center">
              <Shield className="w-6 h-6 text-[#163321]" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-medium text-[#0F1F14]">Informativa sulla Privacy</h1>
              <p className="text-xs text-[#4A5E51] mt-0.5" data-testid="privacy-last-update">
                Ultima revisione: <strong>{PRIVACY_LAST_UPDATE}</strong>
              </p>
              <p className="text-xs text-[#4A5E51]">Ultimo aggiornamento: {new Date().toLocaleDateString("it-IT")}</p>
            </div>
          </div>

          {/* REGISTRO DINAMICO DEI RESPONSABILI DEL TRATTAMENTO */}
          <div className="mb-10 pb-10 border-b border-[#E5E2DB]">
            <PrivacyRegistry showCookies={false} />
          </div>

          <div id="policy-content" className="prose prose-sm max-w-none text-[#4A5E51] [&_h2]:font-heading [&_h2]:text-base [&_h2]:font-medium [&_h2]:text-[#0F1F14] [&_h2]:mt-8 [&_h2]:mb-3 [&_h3]:font-heading [&_h3]:text-sm [&_h3]:font-medium [&_h3]:text-[#0F1F14] [&_h3]:mt-5 [&_h3]:mb-2 [&_p]:text-sm [&_p]:leading-relaxed [&_p]:mb-3 [&_ul]:text-sm [&_ul]:mb-3 [&_li]:mb-1 [&_table]:text-xs [&_table]:my-4 [&_th]:bg-[#F5F4F0] [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:font-medium [&_th]:text-[#0F1F14] [&_td]:px-3 [&_td]:py-2 [&_td]:border [&_td]:border-[#E5E2DB] [&_th]:border [&_th]:border-[#E5E2DB]">

            <h2>1. Titolare del Trattamento</h2>
            <p>
              <strong>CHRISTIAN E CIRO COLUCCI S.A.S.</strong><br />
              Sede legale: Via Armando Diaz 182/L, 80055 Portici (NA)<br />
              P.IVA: 07618011212 — RUI: A000464752<br />
              Email: <a href="mailto:colucci@colucciassicuratori.com" className="text-[#163321] underline">colucci@colucciassicuratori.com</a><br />
              Tel: +39 081 7611000
            </p>

            <h2>2. Dati Personali Raccolti</h2>
            <p>Attraverso il presente sito web raccogliamo esclusivamente i dati necessari per fornire i nostri servizi di consulenza assicurativa:</p>

            <h3>2.1 Dati forniti volontariamente dall'utente</h3>
            <table>
              <thead><tr><th>Dato</th><th>Finalita'</th><th>Obbligatorio</th></tr></thead>
              <tbody>
                <tr><td>Nome e Cognome</td><td>Identificazione del richiedente</td><td>Si'</td></tr>
                <tr><td>Email</td><td>Comunicazioni relative alla richiesta</td><td>Si'</td></tr>
                <tr><td>Telefono</td><td>Contatto diretto per la consulenza</td><td>Si'</td></tr>
                <tr><td>Dati dei questionari</td><td>Analisi del profilo assicurativo</td><td>Si'</td></tr>
                <tr><td>Documenti caricati (PDF, JPG, PNG)</td><td>Valutazione della copertura assicurativa</td><td>Facoltativo</td></tr>
                <tr><td>Dati di pagamento</td><td>Elaborazione del pagamento consulenza</td><td>Solo per consulenza a pagamento</td></tr>
                <tr><td>Dati di prenotazione (data, ora)</td><td>Pianificazione appuntamento consulenza</td><td>Solo per consulenza a pagamento</td></tr>
              </tbody>
            </table>

            <h3>2.2 Dati raccolti automaticamente</h3>
            <table>
              <thead><tr><th>Dato</th><th>Finalita'</th><th>Conservazione</th></tr></thead>
              <tbody>
                <tr><td>Hash IP (anonimizzato, SHA-256 troncato)</td><td>Protezione da abusi e rate limiting</td><td>In memoria, non persistente</td></tr>
              </tbody>
            </table>

            <p><strong>Non raccogliamo</strong>: dati di navigazione, cookie di profilazione, dati di geolocalizzazione, dati biometrici. I <strong>dati sensibili ai sensi dell'art. 9 GDPR</strong> sono raccolti esclusivamente quando strettamente necessari per specifiche coperture sanitarie (es. polizza salute "RealmenteInSalute"), forniti volontariamente dall'utente con consenso esplicito ai sensi dell'art. 9 c.2 lett. a) GDPR e protetti come descritto al §2.3.</p>

            <h3>2.3 Dati sanitari particolari (art. 9 GDPR) — quotatore RiS</h3>
            <p>Il questionario sanitario sintetico raccolto sul wizard del prodotto <strong>"RealmenteInSalute"</strong> (RiS) è soggetto alle seguenti misure tecniche e organizzative aggiuntive:</p>
            <ul>
              <li><strong>Cifratura at-rest AES-256-GCM</strong>: i campi sanitari (patologie dichiarate, terapie, ricoveri, BMI calcolato) sono cifrati al volo prima del salvataggio in database. Nel record persistente resta solo il blob cifrato + metadata non sensibili (flag aggregati, timestamp, algoritmo). La chiave di cifratura è custodita in variabile d'ambiente protetta e mai loggata.</li>
              <li><strong>Cifratura in transit (HTTPS/TLS)</strong> garantita dall'ingress controller e da HSTS strict-transport.</li>
              <li><strong>Consenso esplicito art. 9 c.2 lett. a) GDPR</strong> obbligatorio per la trasmissione del questionario, validato server-side (in assenza di consenso il form viene rifiutato con 422).</li>
              <li><strong>Diritto all'oblio oncologico (L. 193/2023)</strong>: l'utente è informato in modo prominente sul wizard che le patologie oncologiche risolte da oltre 10 anni (5 anni se diagnosticate prima dei 21 anni di età) possono <em>non essere dichiarate</em>.</li>
              <li><strong>Decryption admin on-demand</strong>: la lettura dei dati cifrati avviene solo da utenti autenticati appartenenti ai ruoli <code>admin</code>, <code>consulente_assicurativo</code> o <code>dpo</code>, con motivazione scritta obbligatoria che viene registrata in un log immutabile (<code>audit_health_data_access</code>) conservato 5 anni.</li>
              <li><strong>Audit DPO</strong>: il Responsabile Protezione Dati può consultare ed esportare in qualsiasi momento il registro completo degli accessi ai dati sanitari (CSV/PDF) per finalità di accountability ex art. 5 c.2 GDPR.</li>
              <li><strong>Minimizzazione</strong>: il dettaglio sanitario contrattuale completo viene comunque raccolto soltanto in fase di emissione polizza, in agenzia, dal consulente IVASS, con specifica informativa rinforzata e consenso scritto.</li>
            </ul>

            <h2>3. Base Giuridica del Trattamento</h2>
            <ul>
              <li><strong>Art. 6(1)(b) GDPR</strong> — Esecuzione di un contratto o misure precontrattuali: elaborazione delle richieste di consulenza assicurativa</li>
              <li><strong>Art. 6(1)(f) GDPR</strong> — Legittimo interesse: sicurezza del sito (rate limiting, validazione file)</li>
              <li><strong>Art. 6(1)(a) GDPR</strong> — Consenso: invio di notifiche WhatsApp tramite Spoki (previo conferimento volontario del numero di telefono)</li>
            </ul>

            <h2>4. Destinatari dei Dati</h2>
            <table>
              <thead><tr><th>Destinatario</th><th>Finalita'</th><th>Localizzazione</th></tr></thead>
              <tbody>
                <tr><td>Stripe, Inc.</td><td>Elaborazione pagamenti sicuri</td><td>USA (Privacy Shield/SCC)</td></tr>
                <tr><td>Spoki S.r.l.</td><td>Notifiche WhatsApp automatiche</td><td>Italia/UE</td></tr>
                <tr><td>Emergent Labs</td><td>Archiviazione sicura documenti (Object Storage)</td><td>UE</td></tr>
                <tr><td>Compagnie assicurative partner</td><td>Emissione preventivi e polizze</td><td>Italia/UE</td></tr>
              </tbody>
            </table>
            <p>I dati non vengono mai venduti a terzi ne' utilizzati per finalita' di marketing diretto non richiesto.</p>

            <h2>5. Conservazione dei Dati</h2>
            <ul>
              <li><strong>Dati dei questionari e documenti</strong>: conservati per la durata necessaria alla consulenza e in ogni caso per il periodo previsto dalla normativa IVASS applicabile (Reg. IVASS 44/2019 art. 8: fino a <strong>10 anni</strong> dalla cessazione del rapporto per la documentazione contrattuale). I tempi di TTL effettivamente applicati alle diverse tipologie di dato sono documentati nella <em>Matrice di Retention</em> interna e revisionati periodicamente</li>
              <li><strong>Dati di pagamento</strong>: conservati per 10 anni come previsto dalla normativa fiscale italiana</li>
              <li><strong>Cookie di autenticazione</strong>: massimo 7 giorni (refresh token), 2 ore (access token)</li>
              <li><strong>Log di sicurezza</strong>: 30 giorni</li>
            </ul>

            <h2>6. Diritti dell'Interessato</h2>
            <p>Ai sensi degli articoli 15-22 del GDPR, l'utente ha diritto di:</p>
            <ul>
              <li><strong>Accesso</strong> — ottenere conferma dell'esistenza dei propri dati e riceverne copia</li>
              <li><strong>Rettifica</strong> — correggere dati inesatti o incompleti</li>
              <li><strong>Cancellazione</strong> — richiedere la cancellazione dei dati (nei limiti degli obblighi di legge)</li>
              <li><strong>Limitazione</strong> — limitare il trattamento in determinati casi</li>
              <li><strong>Portabilita'</strong> — ricevere i propri dati in formato strutturato</li>
              <li><strong>Opposizione</strong> — opporsi al trattamento basato su legittimo interesse</li>
              <li><strong>Revoca del consenso</strong> — revocare il consenso in qualsiasi momento</li>
            </ul>
            <p>Per esercitare i propri diritti, contattare: <a href="mailto:colucci@colucciassicuratori.com" className="text-[#163321] underline">colucci@colucciassicuratori.com</a></p>
            <p>L'utente ha inoltre il diritto di proporre reclamo al Garante per la Protezione dei Dati Personali (<a href="https://www.garanteprivacy.it" target="_blank" rel="noopener noreferrer" className="text-[#163321] underline">www.garanteprivacy.it</a>).</p>

            <h2>6-bis. Responsabile della Protezione dei Dati (DPO)</h2>
            <p>
              Il Titolare valuterà la nomina di un <strong>Responsabile della Protezione dei Dati</strong> (Data Protection Officer, DPO) in base all&apos;evoluzione dei trattamenti gestiti e ai criteri previsti dall&apos;art. 37 del Regolamento (UE) 2016/679.
            </p>
            <p>
              In caso di richieste ai sensi degli artt. 15-22 GDPR, di segnalazioni di data breach o di richieste inerenti al whistleblowing (D.Lgs. 24/2023), è possibile scrivere direttamente al Titolare all&apos;indirizzo <a href="mailto:colucci@colucciassicuratori.com" className="text-[#163321] underline">colucci@colucciassicuratori.com</a>. Il Titolare risponderà nei termini di legge (30 giorni prorogabili di ulteriori 60 in casi complessi).
            </p>

            <h2>7. Sicurezza dei Dati</h2>
            <p>Adottiamo misure tecniche e organizzative adeguate per proteggere i dati personali:</p>
            <ul>
              <li>Crittografia in transito (HTTPS/TLS)</li>
              <li>Cookie di autenticazione HttpOnly con flag SameSite</li>
              <li>Validazione rigorosa dei file caricati (estensione, MIME type, magic bytes)</li>
              <li>Rate limiting su tutte le operazioni sensibili</li>
              <li>Anonimizzazione degli indirizzi IP nei log</li>
              <li>Nessun dato personale esposto nelle risposte API pubbliche</li>
              <li>Header di sicurezza HSTS, X-Frame-Options, Content-Security</li>
              <li>Accesso admin protetto da JWT con scadenza temporale</li>
            </ul>

            <h2>8. Trasferimento Dati Extra-UE</h2>
            <p>L'unico trasferimento di dati al di fuori dell'UE avviene verso Stripe, Inc. (USA) per l'elaborazione dei pagamenti, sulla base delle Standard Contractual Clauses (SCC) approvate dalla Commissione Europea e del Data Privacy Framework.</p>

            <h2>9. Modifiche alla Privacy Policy</h2>
            <p>Ci riserviamo il diritto di modificare questa informativa. Eventuali modifiche saranno pubblicate su questa pagina con aggiornamento della data di ultima modifica.</p>

          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
